/**
 * The proofreading suggestion popover (docs/ux/workspace.md, "Proofreading";
 * D-0059): the issue's category and description, the replacements, Ignore,
 * Add to dictionary where that applies, and Previous and Next through the
 * box's issues so every one is reachable by keyboard. A card beside the word
 * on the desktop; a bottom sheet with 44 px controls on the phone. Nothing is
 * applied until a replacement is chosen.
 */

import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { pluralise } from '@/lib/format';
import type { Issue } from './proofreading';

export type ProofPopoverProps = {
  issue: Issue;
  index: number;
  total: number;
  /** Where the click landed, relative to the box's textarea wrapper; the card opens under it. */
  at: { x: number; y: number } | null;
  phone: boolean;
  busy: boolean;
  canAddWord: boolean;
  onApply: (replacement: number) => void;
  onIgnore: () => void;
  onAddWord: () => void;
  onStep: (delta: 1 | -1) => void;
  onClose: () => void;
};

export function ProofPopover(p: ProofPopoverProps) {
  const first = useRef<HTMLButtonElement>(null);
  useEffect(() => { first.current?.focus(); }, [p.issue.id]);

  const body = (
    <div className="flex flex-col gap-3" data-slot="proof-popover" data-kind={p.issue.kind}>
      <div className="flex flex-col gap-1">
        <p className="eyebrow text-beam-text-muted">{p.issue.kindPretty}</p>
        <p className="text-sm">{p.issue.message}</p>
        <p className="text-[13px] text-beam-text-muted">Written: <span className="font-medium text-foreground">{p.issue.problem || '(nothing)'}</span></p>
      </div>
      <div className="flex flex-wrap gap-2">
        {p.issue.replacements.length === 0 ? <p className="text-[13px] text-beam-text-muted">No replacement to offer; edit the words yourself or ignore this.</p> : null}
        {p.issue.replacements.map((r, i) => (
          <Button key={`${i}-${r}`} ref={i === 0 ? first : undefined} size="sm" data-primary={i === 0 ? 'true' : undefined} variant={i === 0 ? 'default' : 'outline'} className={p.phone ? 'h-11' : 'h-8'} disabled={p.busy} data-slot="proof-apply" onClick={() => p.onApply(i)}>
            {r === '' ? 'Remove it' : `Replace with "${r}"`}
          </Button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button ref={p.issue.replacements.length === 0 ? first : undefined} size="sm" variant="outline" className={p.phone ? 'h-11' : 'h-8'} disabled={p.busy} data-slot="proof-ignore" onClick={p.onIgnore}>Ignore</Button>
        {p.canAddWord && p.issue.kind === 'Spelling' && /^[\p{L}\p{N}'-]+$/u.test(p.issue.problem) ? (
          <Button size="sm" variant="outline" className={p.phone ? 'h-11' : 'h-8'} disabled={p.busy} data-slot="proof-add-word" onClick={p.onAddWord}>Add to dictionary</Button>
        ) : null}
        <span className="ml-auto flex items-center gap-1 text-[13px] text-beam-text-muted">
          <Button size="sm" variant="ghost" className={p.phone ? 'h-11 w-11' : 'h-8 w-8'} aria-label="Previous suggestion" disabled={p.total < 2} onClick={() => p.onStep(-1)}>{'<'}</Button>
          <span className="tabular">{p.index + 1} of {p.total}</span>
          <Button size="sm" variant="ghost" className={p.phone ? 'h-11 w-11' : 'h-8 w-8'} aria-label="Next suggestion" disabled={p.total < 2} onClick={() => p.onStep(1)}>{'>'}</Button>
        </span>
      </div>
      {!p.canAddWord && p.issue.kind === 'Spelling' ? <p className="text-xs text-beam-text-muted">Adding to the dictionary is the doctor's; Ignore hides this suggestion for you.</p> : null}
    </div>
  );

  if (p.phone) {
    return (
      <Sheet open onOpenChange={(o) => { if (!o) p.onClose(); }}>
        <SheetContent side="bottom" className="max-h-[85svh] rounded-t-[20px] shadow-beam-3">
          <div aria-hidden="true" className="mx-auto mt-2 h-1 w-10 rounded-full bg-beam-line-strong" />
          <SheetHeader className="pb-0"><SheetTitle className="text-base">Proofreading suggestion</SheetTitle><SheetDescription className="sr-only">{pluralise(p.total, 'suggestion')} in this box</SheetDescription></SheetHeader>
          <div className="px-4 pb-6 pt-3">{body}</div>
        </SheetContent>
      </Sheet>
    );
  }

  // Below the box, aligned to the word that was clicked: the card never covers the words it is about (audit, 9 September 2026).
  const left = Math.max(0, Math.min(p.at?.x ?? 0, 480));
  return (
    <div
      role="dialog"
      aria-label="Proofreading suggestion"
      className="absolute top-full z-20 mt-2 w-[22rem] max-w-[calc(100%-1rem)] rounded-lg border border-beam-line bg-beam-paper p-3 shadow-beam-2"
      style={{ left }}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); p.onClose(); } }}
    >
      {body}
      <Button size="sm" variant="ghost" className="absolute right-2 top-2 h-7 w-7" aria-label="Close the suggestion" onClick={p.onClose}>x</Button>
    </div>
  );
}
